/* import __COLOCATED_TEMPLATE__ from './ai-agent-event.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type AiAgentEvent from 'embercom/objects/inbox/renderable/ai-agent-event';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import type RouterService from '@ember/routing/router-service';
import { type AiAgentEventData } from 'embercom/objects/inbox/ai-agent-event-data';
import { AiAgentEventType } from 'embercom/objects/inbox/renderable/ai-agent-event';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { truncateEventText, isEventTextTruncated } from 'embercom/lib/inbox2/event-text-truncation';
import type Conversation from 'embercom/objects/inbox/conversation';
import {
  resolveStepReasoning,
  joinStepNumbers,
  generateContentUrl,
} from 'embercom/components/inbox2/conversation-stream/helpers/fin-thoughts-event';

interface AiAgentEventPart extends RenderablePart {
  renderableData: AiAgentEvent;
}

interface Signature {
  Args: {
    part: AiAgentEventPart;
    conversation: Conversation;
  };
}

export default class Inbox2ConversationStreamEventPartsAiAgentEventComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: Session;
  @service declare router: RouterService;
  @service declare intercomEventService: any;

  @tracked openReasoningSection = '';
  @tracked openStepReasoningSection = '';
  @tracked openCodeErrorSection = '';
  @tracked openContentRetrievalSection = '';
  @tracked openCodeBlockOutputSection = '';

  get EVENT_TYPES() {
    // expose the AiAgentEventType enum for use in the template
    return AiAgentEventType;
  }

  get eventType() {
    return this.args.part.renderableData.eventType;
  }

  get eventDataFromBackend(): AiAgentEventData[keyof AiAgentEventData] | {} {
    // Get the event data passed from the backend for this event
    // You can override any of these by setting them in the additionalEventData method below
    let eventData = this.args.part.renderableData.eventData;

    if (eventData && this.eventType in eventData) {
      return eventData[this.eventType as keyof AiAgentEventData] || {};
    } else {
      return {};
    }
  }

  get isTaskEvent() {
    return (
      this.eventType === AiAgentEventType.AiAgentWorkflowStarted ||
      this.eventType === AiAgentEventType.AiAgentWorkflowFinished ||
      this.eventType === AiAgentEventType.AiAgentWorkflowFailed ||
      this.eventType === AiAgentEventType.AiAgentWorkflowStopped
    );
  }

  get additionalEventData(): { icon?: InterfaceIconName } {
    // Add any additional parameters to pass as a hash to the i18n helper
    switch (this.eventType) {
      case AiAgentEventType.OtpEmailVerificationSuccessful:
        return { icon: 'locked' };
      case AiAgentEventType.OtpEmailVerificationFailed:
      case AiAgentEventType.OtpEmailVerificationUnsuccessful:
        return { icon: 'locked' };
      case AiAgentEventType.OtpEmailAlreadyVerified:
        return { icon: 'locked' };
      case AiAgentEventType.OtpEmailTriggeredForVerification:
        return { icon: 'data-connectors' };
      case AiAgentEventType.AiAgentWorkflowStarted:
      case AiAgentEventType.AiAgentWorkflowFinished:
      case AiAgentEventType.AiAgentWorkflowFailed:
      case AiAgentEventType.AiAgentWorkflowStopped:
        return { icon: 'custom-attribute' };
      case AiAgentEventType.SdrRoutingDecision:
        return { icon: 'transfer' };
      case AiAgentEventType.SdrRecoveryEmailSent:
        return { icon: 'email' };
      case AiAgentEventType.AiAgentSetAttributes:
        return { icon: 'transfer' };
      case AiAgentEventType.AiAgentTaskStepReasoning:
        return { icon: 'fin' };
      case AiAgentEventType.AiAgentConditionalEvaluation:
      case AiAgentEventType.AiAgentGotoSection:
      case AiAgentEventType.AiAgentEndSection:
        return { icon: 'branch' };
      case AiAgentEventType.AiAgentContentRetrieval:
        return { icon: 'article' };
      case AiAgentEventType.AiAgentCodeConditionError:
        return { icon: 'alert' };
      case AiAgentEventType.AiAgentFollowUpScheduled:
        return { icon: 'fin' };
      case AiAgentEventType.AiAgentCodeBlockStarted:
      case AiAgentEventType.AiAgentCodeBlockFinished:
        return { icon: 'code-block' };
      case AiAgentEventType.AiAgentIgnoringUserMessage:
        return { icon: 'alert-circle' };
      case AiAgentEventType.AiAgentHitlReviewStarted:
      case AiAgentEventType.AiAgentHitlReviewFinished:
        return { icon: 'fin' };
      case AiAgentEventType.SdrMeetingBooked:
        return { icon: 'calendar' };
      default:
        return {};
    }
  }

  get eventData() {
    return { ...this.eventDataFromBackend, ...this.additionalEventData };
  }

  get eventIcon(): InterfaceIconName {
    // We only use this in contexts that already guard on the presence of an icon
    // (e.g. `{{else if this.eventData.icon}}` in the template), so assert non-null
    return (this.eventData as { icon?: InterfaceIconName }).icon as InterfaceIconName;
  }

  get translatedEvent() {
    if (this.eventType === AiAgentEventType.AiAgentIgnoringUserMessage) {
      return this.intl.t(
        `inbox.ai-agent-events.${this.eventType}.${(this.eventData as AiAgentEventData[AiAgentEventType.AiAgentIgnoringUserMessage])?.reason || 'unknown'}`,
      );
    }

    if (Object.keys(this.eventData).includes('icon')) {
      // If there's an {icon} in your translated string, we need to use ic-interface-icon helper in the template directly as we can't do it here
      // You'll need to add an if case in the template and pass all the other params manually as a hash in the template to the {{t}} helper
      return null;
    } else if (Object.keys(this.eventData).length > 0) {
      return this.intl.t(`inbox.ai-agent-events.${this.eventType}`, this.eventData);
    } else {
      return this.intl.t(`inbox.ai-agent-events.${this.eventType}`);
    }
  }

  get reasoningText(): string {
    if (this.eventType === AiAgentEventType.AiCategoriesReasoning) {
      return (this.eventData as { reasoning: string })?.reasoning || '';
    }
    if (this.eventType === AiAgentEventType.AiAgentWorkflowStarted) {
      return (this.eventData as { reasoning?: string })?.reasoning || '';
    }
    if (this.eventType === AiAgentEventType.AiAgentWorkflowFailed) {
      let reasoning = (this.eventData as { reasoning?: string })?.reasoning;
      if (reasoning === 'system_error') {
        return this.intl.t('inbox.ai-agent-events.ai_agent_workflow_failed_system_error');
      }
      return reasoning || '';
    }
    return '';
  }

  get hitlReviewI18nKey(): string {
    if (this.eventType === AiAgentEventType.AiAgentHitlReviewStarted) {
      return 'inbox.ai-agent-events.ai_agent_hitl_review_started';
    }
    return 'inbox.ai-agent-events.ai_agent_hitl_review_finished';
  }

  get routingReason(): string {
    if (this.eventType === AiAgentEventType.SdrRoutingDecision) {
      return (this.eventData as { routingOutcomeReason: string })?.routingOutcomeReason || '';
    }
    return '';
  }

  get attributes() {
    if (this.eventType === AiAgentEventType.AiAgentSetAttributes) {
      return (
        (this.eventData as AiAgentEventData[AiAgentEventType.AiAgentSetAttributes])?.attributes ||
        []
      );
    }
    return [];
  }

  get firstAttribute() {
    return this.attributes[0];
  }

  get remainingAttributesCount() {
    return Math.max(0, this.attributes.length - 1);
  }

  @action
  toggleReasoningSection(sectionId: string) {
    this.openReasoningSection = this.openReasoningSection === sectionId ? '' : sectionId;
  }

  truncateTitle(title: string | undefined): string {
    return truncateEventText(title);
  }

  isTitleTruncated(title: string | undefined): boolean {
    return isEventTextTruncated(title);
  }

  // Type-safe getters for template usage
  get eventDataTitle(): string | undefined {
    return (this.eventData as { title?: string }).title;
  }

  get eventDataRuleName(): string | undefined {
    return (this.eventData as { ruleName?: string }).ruleName;
  }

  get eventDataRulesetId(): string | undefined {
    return (this.eventData as { rulesetId?: string }).rulesetId;
  }

  get operatorName(): string {
    // To Do: investigate how to get the operator name from the event data
    // Workflow events have an operator name in the event data, but AI agent events do not
    return this.intl.t('inbox.operator');
  }

  // Procedure event data getters — type-safe access to each sub-event's data
  get stepReasoningData() {
    let rawData =
      this.args.part.renderableData.eventData[AiAgentEventType.AiAgentTaskStepReasoning];
    if (!rawData) {
      return undefined;
    }

    let result = resolveStepReasoning(rawData.reasonedStepIds ?? [], rawData.stepNumber);
    if (!result) {
      return undefined;
    }

    let i18nKey;
    if (result.i18nKeySuffix === 'ai_agent_task_step_reasoning') {
      i18nKey = 'inbox.ai-agent-events.ai_agent_task_step_reasoning';
    } else if (result.i18nKeySuffix === 'ai_agent_task_step_reasoning_plural') {
      i18nKey = 'inbox.ai-agent-events.ai_agent_task_step_reasoning_plural';
    } else if (result.i18nKeySuffix === 'ai_agent_task_step_reasoning_sub_procedure') {
      i18nKey = 'inbox.ai-agent-events.ai_agent_task_step_reasoning_sub_procedure';
    } else {
      i18nKey = 'inbox.ai-agent-events.ai_agent_task_step_reasoning_sub_procedure_plural';
    }

    return {
      i18nKey,
      stepNumber: result.stepNumber,
      subProcedure: result.subProcedure,
      reason: rawData.reason,
    };
  }

  get conditionalEvaluationData() {
    let rawData =
      this.args.part.renderableData.eventData[AiAgentEventType.AiAgentConditionalEvaluation];
    if (!rawData) {
      return undefined;
    }

    let rawStepNumber = rawData.stepNumber;
    let skipped = rawData.skippedCondition;
    let match = rawStepNumber?.match(/^(.+)\.(\d+)$/);
    let subProcedure = match ? match[1] : null;
    if (subProcedure === 'main_section') {
      subProcedure = null;
    }
    let stepNumber = match ? match[2] : rawStepNumber;

    let i18nKey;
    if (skipped && subProcedure) {
      i18nKey = 'inbox.ai-agent-events.ai_agent_conditional_evaluation_skipped';
    } else if (skipped && !subProcedure) {
      i18nKey = 'inbox.ai-agent-events.ai_agent_conditional_evaluation_skipped_main_procedure';
    } else if (!skipped && subProcedure) {
      i18nKey = 'inbox.ai-agent-events.ai_agent_conditional_evaluation';
    } else {
      i18nKey = 'inbox.ai-agent-events.ai_agent_conditional_evaluation_main_procedure';
    }

    return {
      i18nKey,
      selectedBranchNumber: String(rawData.selectedBranchNumber),
      stepNumber,
      subProcedure: subProcedure || undefined,
    };
  }

  get contentRetrievalData() {
    let rawData = this.args.part.renderableData.eventData[AiAgentEventType.AiAgentContentRetrieval];
    if (!rawData) {
      return undefined;
    }
    return rawData;
  }

  get retrievedContentWithUrls() {
    let rawData = this.contentRetrievalData;
    if (!rawData?.retrievedContent) {
      return [];
    }

    let appId = this.session.workspace?.id;
    let urlFor = this.router.urlFor.bind(this.router);
    return rawData.retrievedContent.map((content) => ({
      ...content,
      url: generateContentUrl(content.entityTypeId, content.entityId, appId, urlFor),
    }));
  }

  get skippedStepsData() {
    let rawData = this.args.part.renderableData.eventData[AiAgentEventType.AiAgentSkippedSteps];
    if (!rawData?.skippedSteps) {
      return undefined;
    }

    let skippedSteps = rawData.skippedSteps;
    let i18nKey = 'inbox.ai-agent-events.ai_agent_skipped_step';
    if (skippedSteps.length > 1) {
      i18nKey = 'inbox.ai-agent-events.ai_agent_skipped_step_plural';
    }

    let stepNumbers = skippedSteps.map((step) => {
      if (step.sectionName === 'Main Section') {
        return step.stepNumber;
      } else {
        return `${step.sectionName} ${step.stepNumber}`;
      }
    });
    let stepNumber = joinStepNumbers(stepNumbers);

    return {
      i18nKey,
      stepNumber,
    };
  }

  get codeConditionErrorData() {
    let rawData =
      this.args.part.renderableData.eventData[AiAgentEventType.AiAgentCodeConditionError];
    if (!rawData) {
      return undefined;
    }
    return rawData;
  }

  get gotoSectionData() {
    let rawData = this.args.part.renderableData.eventData[AiAgentEventType.AiAgentGotoSection];
    if (!rawData) {
      return undefined;
    }
    return {
      sectionName: rawData.sectionName || this.intl.t('inbox.ai-agent-events.sub_procedure'),
    };
  }

  get followUpScheduledData() {
    let rawData =
      this.args.part.renderableData.eventData[AiAgentEventType.AiAgentFollowUpScheduled];
    if (!rawData) {
      return undefined;
    }
    return rawData;
  }

  get followUpTurnOffUrl(): string | undefined {
    let data = this.followUpScheduledData;
    if (!data) {
      return undefined;
    }
    let appId = this.session.workspace.id;
    if (data.source === 'simple_deploy') {
      return `/a/apps/${appId}/automation/fin-ai-agent/deploy/email`;
    }
    if (!data.rulesetId) {
      return undefined;
    }
    let url = `/a/apps/${appId}/automation/workflows/${data.rulesetId}`;
    if (data.stepId) {
      url += `?step_id=${data.stepId}`;
    }
    return url;
  }

  get endSectionData() {
    let rawData = this.args.part.renderableData.eventData[AiAgentEventType.AiAgentEndSection];
    if (!rawData) {
      return undefined;
    }
    return {
      sectionName: rawData.sectionName || this.intl.t('inbox.ai-agent-events.sub_procedure'),
      returnedToSectionName:
        rawData.returnedToSectionName || this.intl.t('inbox.ai-agent-events.main_procedure'),
    };
  }

  get codeBlockStartedData() {
    let rawData = this.args.part.renderableData.eventData[AiAgentEventType.AiAgentCodeBlockStarted];
    if (!rawData) {
      return undefined;
    }
    return rawData;
  }

  get codeBlockFinishedData() {
    let rawData =
      this.args.part.renderableData.eventData[AiAgentEventType.AiAgentCodeBlockFinished];
    if (!rawData) {
      return undefined;
    }
    return rawData;
  }

  get codeBlockFinishedHasExpandableContent(): boolean {
    let data = this.codeBlockFinishedData;
    if (!data) {
      return false;
    }
    return !data.success || Boolean(data.output);
  }

  get formattedCodeBlockOutput(): string {
    let output = this.codeBlockFinishedData?.output;
    if (!output) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(output), null, 2);
    } catch {
      return output;
    }
  }

  @action
  toggleCodeBlockOutputSection() {
    this.openCodeBlockOutputSection =
      this.openCodeBlockOutputSection === 'open_code_block_output' ? '' : 'open_code_block_output';
  }

  @action
  toggleStepReasoningSection() {
    this.openStepReasoningSection =
      this.openStepReasoningSection === 'open_reasoning_step' ? '' : 'open_reasoning_step';
  }

  @action
  toggleCodeErrorSection() {
    this.openCodeErrorSection =
      this.openCodeErrorSection === 'open_code_error' ? '' : 'open_code_error';
  }

  @action
  toggleContentRetrievalSection() {
    this.openContentRetrievalSection =
      this.openContentRetrievalSection === 'open_content_retrieval' ? '' : 'open_content_retrieval';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::AiAgentEvent': typeof Inbox2ConversationStreamEventPartsAiAgentEventComponent;
    'inbox2/conversation-stream/event-parts/ai-agent-event': typeof Inbox2ConversationStreamEventPartsAiAgentEventComponent;
  }
}
